if (!window.customElements.get('choose-offer-form')) {
  const WINBACKS_SELECTED_OFFER_STORAGE_KEY = 'winbacks:selected-offer';
  const WINBACKS_SELECTED_DISCOUNT_STORAGE_KEY = 'winbacks:selected-reference';
  const WINBACKS_PARENT_SUBSCRIPTION_STORAGE_KEY = 'winbacks:parent-subscription';

  /* Price+ smoke test: the treatment arm advertises its own offer code instead of
     the page's `lander_discount_code` metafield. Control is left untouched.
     The arm resolves asynchronously (Optimizely loads async), so theme.js
     publishes the multi-experiment snapshot on `window.experiments` (keyed by
     registry slug) and fires `experiments:changed` for non-module assets like
     this one — see resolveExperimentArms.js. The slug must match
     PRICE_PLUS_SLUG in src/scripts/builder/hooks/usePricePlusArm.js. */
  const PRICE_PLUS_SLUG = 'price-plus';
  const PRICE_PLUS_ARM = 'v1';

  /* Read the Price+ arm off a snapshot; an unknown/unregistered slug is control. */
  function pricePlusArm(experiments) {
    const decision = experiments && experiments[PRICE_PLUS_SLUG];
    return decision ? decision.variation : 'control';
  }
  const PRICE_PLUS_OFFER_CODE = 'AUGUST20';

  /* Rewrite the `reference` param on an already-rendered URL, leaving variant-id,
     product-id and step2Redirect alone. Adds the param when the page had no
     lander discount code at all. */
  function setReferenceParam(element, property, code) {
    if (!element || !element[property]) return;
    try {
      const url = new URL(element[property], window.location.origin);
      url.searchParams.set('reference', code);
      element[property] = `${url.pathname}${url.search}`;
    } catch (error) {
      // A malformed or empty URL is left as-is rather than blanked. Unlike the
      // localStorage catches below, this is never an expected browser condition —
      // it means the theme rendered a bad entry-gate URL, so make it visible.
      console.warn('Unable to set the reference param on the entry-gate URL.', error);
    }
  }

  class ChooseOfferForm extends HTMLElement {
    constructor() {
      super();
      this.offerTiles = this.querySelectorAll('[data-offer-tile]');
      this.form = this.querySelector('#newsletter-form');
      this.emailInput = this.querySelector('input[name="contact[email]"]');
      this.builderUrl = this.dataset.builderUrl;
      this.discount = this.querySelector('[data-reference-input]')?.value;
      this.isWinbacksFlow = this.dataset.winbacksFlow === 'true';
      this.submitButton = this.querySelector('[data-cyo-submit]');
    }
    connectedCallback() {
      const listContainer = this.querySelector('[role="list"]');
      if (listContainer) {
        listContainer.setAttribute('role', 'radiogroup');
        listContainer.setAttribute('aria-label', 'Choose an offer');
      }

      if (this.submitButton) {
        this.submitButton.addEventListener('click', () => this.handleRemoveStoredDiscount());
      }
      this.offerTiles.forEach((tile) => {
        const isSelected = tile.classList.contains('selected');
        tile.setAttribute('tabindex', isSelected ? '0' : '-1');

        tile.addEventListener('click', (e) => this.handleOfferSelect(e.currentTarget));
        tile.addEventListener('keydown', (e) => this.handleKeydown(e));
      });

      if (this.isWinbacksFlow) {
        this.persistSelectedOffer();
        if (this.discount) {
          this.persistDiscount(this.discount);
        }
        this.registerSubmitHandlers();

        this.preloadParentSubscription();
      } else {
        this.handleRemoveStoredDiscount();
      }

      if (this.form && this.emailInput) {
        this.form.addEventListener('submit', () => {
          window.localStorage.setItem('chooseOfferEmail', this.emailInput.value);
        });
      }

      // The arm may already be resolved (persisted in sessionStorage from an
      // earlier page in the funnel) or may land later, once Optimizely decides.
      this.applyArmOfferCode(pricePlusArm(window.experiments));
      this.handleArmResolved = (event) => this.applyArmOfferCode(pricePlusArm(event.detail?.experiments));
      window.addEventListener('experiments:changed', this.handleArmResolved);
    }

    disconnectedCallback() {
      if (this.handleArmResolved) {
        window.removeEventListener('experiments:changed', this.handleArmResolved);
      }
    }

    /* Swap in the Price+ offer code for the treatment arm. Updates the cached
       copy the constructor read, the hidden input a later read would use, and
       both already-rendered entry-gate URLs (the email gate's return_to and the
       no-email CTA's href — which one is visible depends on a page metafield). */
    applyArmOfferCode(arm) {
      if (this.isWinbacksFlow) return;
      if (arm !== PRICE_PLUS_ARM || this.discount === PRICE_PLUS_OFFER_CODE) return;

      this.discount = PRICE_PLUS_OFFER_CODE;

      const referenceInput = this.querySelector('[data-reference-input]');
      if (referenceInput) referenceInput.value = PRICE_PLUS_OFFER_CODE;

      const returnToInput = this.form?.querySelector('input[name="return_to"]');
      const noEmailLink = this.querySelector('[data-entry-gate="no-email"] a[data-submit]') || this.querySelector('a[data-submit]');

      setReferenceParam(returnToInput, 'value', PRICE_PLUS_OFFER_CODE);
      setReferenceParam(noEmailLink, 'href', PRICE_PLUS_OFFER_CODE);
    }

    handleRemoveStoredDiscount() {
      window.localStorage.removeItem('subscriptionBuilderDiscount');
    }

    buildBuilderUrl(variantId, productId) {
      const params = new URLSearchParams();
      params.set('variant-id', variantId);
      params.set('product-id', productId);
      if (this.discount) params.set('reference', this.discount);
      if (this.hasAttribute('data-go-to-step-2')) params.set('step2Redirect', 'true');
      return `${this.builderUrl}?${params.toString()}`;
    }

    handleOfferSelect(tile) {
      this.offerTiles.forEach((t) => {
        t.classList.remove('selected');
        t.setAttribute('aria-checked', 'false');
        t.setAttribute('tabindex', '-1');
        const radio = t.querySelector('[data-offer-radio]');
        if (radio) radio.checked = false;
      });

      tile.classList.add('selected');
      tile.setAttribute('aria-checked', 'true');
      tile.setAttribute('tabindex', '0');

      const radio = tile.querySelector('[data-offer-radio]');

      if (radio) radio.checked = true;

      const variantId = tile.dataset.variantId;
      const productId = tile.dataset.offerProductId;
      const hasValidIds = Boolean(variantId && productId);

      // Keep both entry paths in sync: the email gate's return_to and the no-email
      // CTA's href (which one shows depends on the Show Email Signup setting).
      const returnToInput = this.form?.querySelector('input[name="return_to"]');
      const noEmailLink = this.querySelector('[data-entry-gate="no-email"] a[data-submit]') || this.querySelector('a[data-submit]');

      if (hasValidIds) {
        const builderUrl = this.buildBuilderUrl(variantId, productId);
        if (returnToInput) returnToInput.value = builderUrl;
        if (noEmailLink) {
          noEmailLink.href = builderUrl;
          noEmailLink.removeAttribute('aria-disabled');
          noEmailLink.classList.remove('disabled');
        }
      } else if (noEmailLink) {
        noEmailLink.setAttribute('aria-disabled', 'true');
        noEmailLink.classList.add('disabled');
      }

      this.persistSelectedOffer(tile);
    }

    handleKeydown(e) {
      const tile = e.currentTarget;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleOfferSelect(tile);
        return;
      }

      const isArrowKey = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key);
      if (!isArrowKey) return;

      e.preventDefault();

      const tiles = Array.from(this.offerTiles);
      const currentIndex = tiles.indexOf(tile);

      let nextIndex = currentIndex;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % tiles.length;
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + tiles.length) % tiles.length;
      }

      const nextTile = tiles[nextIndex];
      if (!nextTile) return;

      nextTile.focus();
    }

    registerSubmitHandlers() {
      this.addEventListener('submit', () => {
        this.persistSelectedOffer();
      });

      const submitButton = this.querySelector('[data-submit]');
      if (submitButton) {
        submitButton.addEventListener('click', () => {
          this.persistSelectedOffer();
        });
      }
    }

    getSelectedOfferTile() {
      return this.querySelector('[data-offer-tile].selected') || this.offerTiles[0] || null;
    }

    persistDiscount(discount) {
      try {
        localStorage.setItem(WINBACKS_SELECTED_DISCOUNT_STORAGE_KEY, discount);
      } catch {
        // localStorage can be unavailable in private mode or restricted browsers.
      }
    }

    persistSelectedOffer(tile = null) {
      const selectedTile = tile || this.getSelectedOfferTile();
      if (!selectedTile) return;

      const variantId = selectedTile.dataset.variantId ? String(selectedTile.dataset.variantId) : null;
      const productId = selectedTile.dataset.offerProductId ? String(selectedTile.dataset.offerProductId) : null;
      if (!variantId || !productId) return;

      try {
        localStorage.setItem(
          WINBACKS_SELECTED_OFFER_STORAGE_KEY,
          JSON.stringify({
            variantId,
            productId,
            savedAt: new Date().toISOString(),
          })
        );
      } catch {
        // localStorage can be unavailable in private mode or restricted browsers.
      }
    }

    async waitForRechargeApi() {
      const maxAttempts = 20;
      let attempts = 0;

      while (!window.ButcherBoxRechargeProductCardApi && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts += 1;
      }

      return window.ButcherBoxRechargeProductCardApi || null;
    }

    async preloadParentSubscription() {
      try {
        const rechargeApi = await this.waitForRechargeApi();
        if (!rechargeApi?.getWinbackParentSubscription) return;

        const parentSubscription = await rechargeApi.getWinbackParentSubscription();
        if (!parentSubscription) return;

        localStorage.setItem(
          WINBACKS_PARENT_SUBSCRIPTION_STORAGE_KEY,
          JSON.stringify({
            ...parentSubscription,
            savedAt: new Date().toISOString(),
          })
        );
      } catch (error) {
        console.warn('Unable to preload winbacks parent subscription.', error);
      }
    }
  }

  window.customElements.define('choose-offer-form', ChooseOfferForm);
}
